import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { springQuick, tapScale } from '../tokens';

type ListRowProps = {
  /** Lucide icon (or any node). Rendered inside a 36px tinted square. */
  icon?: ReactNode;
  /** Tailwind text color class for the icon — pairs with iconBgClass. */
  iconColorClass?: string;
  /** Tailwind background class for the icon container. */
  iconBgClass?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned value (e.g. "$1,234"). */
  value?: ReactNode;
  /** Trailing accessory; defaults to chevron when onClick is present. */
  accessory?: 'chevron' | 'none' | ReactNode;
  onClick?: () => void;
  destructive?: boolean;
  className?: string;
};

export function ListRow({
  icon,
  iconColorClass = 'text-text-secondary',
  iconBgClass = 'bg-surface-secondary',
  title,
  subtitle,
  value,
  accessory,
  onClick,
  destructive = false,
  className,
}: ListRowProps) {
  const interactive = !!onClick;

  const trailing =
    accessory === 'none'
      ? null
      : accessory === 'chevron' || (interactive && accessory === undefined)
        ? <ChevronRight size={18} className="text-text-tertiary shrink-0" />
        : accessory;

  const content = (
    <>
      {icon ? (
        <div
          className={cn(
            'w-9 h-9 rounded-button shrink-0 inline-flex items-center justify-center',
            iconBgClass,
            iconColorClass,
          )}
        >
          {icon}
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-[15px] font-medium truncate',
            destructive ? 'text-negative' : 'text-text-primary',
          )}
        >
          {title}
        </div>
        {subtitle ? (
          <div className="text-[13px] text-text-secondary truncate mt-0.5">
            {subtitle}
          </div>
        ) : null}
      </div>
      {value !== undefined && value !== null ? (
        <div className="text-[15px] font-mono text-text-primary tabular-nums shrink-0">
          {value}
        </div>
      ) : null}
      {trailing}
    </>
  );

  const baseClasses = cn(
    'w-full flex items-center gap-3 min-h-tap py-2 px-card-pad text-left',
    className,
  );

  if (interactive) {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        whileTap={tapScale}
        transition={springQuick}
        className={baseClasses}
      >
        {content}
      </motion.button>
    );
  }

  return <div className={baseClasses}>{content}</div>;
}
